import React from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import { generatePath, matchPath, Redirect, Switch, useHistory, useLocation, useParams } from 'react-router-dom';
import { FormApi } from 'final-form';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { find } from 'lodash';
import { useSession } from '@lendi/lala-react';

import { URL_PARAM_APPLICATION_ID } from '@funnels/constants';
import { useCustomer, useRouter } from '@funnels/hooks';
import { useAnalytics } from '@funnels/providers';
import { EntityIndices, FunnelConfig, OptionalTargetParams, QuestionConfig, UseParams } from '@funnels/types';
import { calculator } from '@funnels/utils';

import FunnelContainer from '../_common/Container';
import ProgressBar from '../ProgressBar';
import { Spinner } from './index.style';

interface SubmittableFormProps<T> extends Partial<EntityIndices> {
  config: FunnelConfig;
  isStartup?: boolean;
  initialValues: T;
  funnelType: 'funnelOne' | 'funnelTwo';
  onSubmit: (nextQuestion: any, values: T, params?: UseParams) => void;
  isNextButtonDisabled?: boolean;
}

export function SubmittableForm<T>(props: SubmittableFormProps<T>) {
  const {
    initialValues,
    config,
    applicantIndex,
    liabilityIndex,
    assetIndex,
    funnelType,
    isNextButtonDisabled,
    financialSituationIndex,
  } = props;
  const LDFlags = useFlags();
  const { tracking } = useAnalytics();
  const customer = useCustomer();
  const params = useParams<UseParams>();
  const location = useLocation();
  const history = useHistory();
  const reduxStoreData = useSelector((currentState) => currentState);
  // Form object can only be accessed inside <From/>.
  // Prevent hacking inner component, get the reference after form rendering
  const formRef = React.useRef<any>(null);
  const { query } = useRouter();
  const isSkipInSearch = query['skip'] === 'true';
  const { [URL_PARAM_APPLICATION_ID]: applicationId } = params;
  const sessionData = useSession();
  const isFunnelOne = funnelType === 'funnelOne';
  const entityIndices: Partial<EntityIndices> = {
    applicantIndex,
    assetIndex,
    liabilityIndex,
    financialSituationIndex,
  };

  // Get the question
  const allQuestions = config.questions.length || 0;
  let questionIndex = 0;
  const theQuestion =
    find(config.questions, (q, index) => {
      const generatedPath = generatePath(q.url, params);
      const match = matchPath(generatedPath, {
        path: location.pathname,
        exact: true,
        strict: false,
      });
      questionIndex = index;

      return !!match;
    }) || ({} as QuestionConfig);
  const { target, skippable, component: Component, pageTitle } = theQuestion;
  // if question is skippable and url has skip in search
  const shouldSkip = skippable && isSkipInSearch;
  // Determine widths
  let progress = 0;

  // Get progress bar length
  if (!props.isStartup) {
    progress = ((questionIndex + 1) / allQuestions) * 100;
  }

  function handleFormSubmit(values: T, form: FormApi<T, T>) {
    const options: OptionalTargetParams = {
      ...entityIndices,
      customerId: customer.data.id || '',
      location,
      sessionData,
      mobileVerified: !!customer.data.mobileVerified,
      LDFlags,
      params,
    };
    const nextPage = target(values, options, reduxStoreData);
    //although shouldSkip===true, if target's ignoreSkip===true, still don't append skip=true in search
    nextPage.url = `${nextPage.url}${shouldSkip && !nextPage.ignoreSkip ? '?skip=true' : ''}`;
    const { dirtyFields } = form.getState();
    tracking?.form(values, dirtyFields);
    props.onSubmit(nextPage, values, params);
  }

  React.useEffect(() => {
    // In Funnel One (no applicationId), we need to enforce the redirection of any question different from
    // the entryPoint on componentDidMount.
    if (!applicationId && questionIndex > 0) {
      history.push(`${config.entryPoint(initialValues, LDFlags, params)}${history.location.search}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    //reduce compare calculation.
    if (!shouldSkip) return;
    const form = formRef.current;
    if (form.getState && form.getState().valid && Component) {
      // user can back to last question, so the url must not have skip
      // if form valid, manually submit the form
      formRef.current.submit();
    }
  }, [location.pathname, shouldSkip, skippable]);

  return (
    <>
      {pageTitle && (
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
      )}
      <Form onSubmit={handleFormSubmit} initialValues={initialValues} decorators={[calculator]}>
        {({ form, handleSubmit, values, dirtyFields }: FormRenderProps<T>) => {
          // form is not an immutable object which means you need to update form reference every rerender
          // otherwise, you will access the form object of last render (the scope is not correct)
          formRef.current = form;
          if (!Component)
            return (
              <Switch>
                <Redirect
                  from={props.config.baseName}
                  to={`${config.entryPoint(values, LDFlags, params)}${
                    isSkipInSearch && isFunnelOne ? '?skip=true' : ''
                  }`}
                />
              </Switch>
            );
          // dirty flag will keep true although user trigger the submit
          // any field changed before submit dirtyFieldsSinceLastSubmit will maintain the key
          const { valid, dirty, dirtyFieldsSinceLastSubmit } = form.getState();
          const haveDirtyFieldSinceLastSubmit = Object.keys(dirtyFieldsSinceLastSubmit).length > 0;
          return (
            <>
              <ProgressBar progress={progress} />
              {/* 
                We need to display spinner if there the page are valid 
                But react-final-form need to be rendered to validate
                So we cannot only display spinner without the detailed question
              */}
              {shouldSkip && ((valid && !dirty) || (valid && !haveDirtyFieldSinceLastSubmit)) && <Spinner />}
              <FunnelContainer shouldHideBackButton={isFunnelOne}>
                <form
                  onSubmit={(e) => {
                    handleSubmit(e);
                  }}
                >
                  <Component
                    setValue={form.change}
                    currentValues={values}
                    triggerFormSubmit={form.submit}
                    isNextButtonDisabled={!!isNextButtonDisabled}
                    {...props}
                    {...entityIndices}
                  />
                </form>
              </FunnelContainer>
            </>
          );
        }}
      </Form>
    </>
  );
}
